import { Box, Paper, Typography } from '@mui/material';
import { YardChecksSummary } from '../../api/yardCheck';

interface Bucket {
  key: 'fresh' | 'stale' | 'cold' | 'notSeen';
  label: string;
  range: string;
  color: string;
}

const BUCKETS: Bucket[] = [
  { key: 'fresh', label: 'Fresh', range: 'under 30m', color: '#2e7d32' },
  { key: 'stale', label: 'Stale', range: '30m – 2h', color: '#f9a825' },
  { key: 'cold', label: 'Cold', range: '2h – 4h', color: '#ef6c00' },
  { key: 'notSeen', label: 'Not seen', range: 'over 4h, or never', color: '#c62828' },
];

const countFor = (summary: YardChecksSummary, key: Bucket['key']): number => {
  switch (key) {
    case 'fresh':
      return summary.freshCount;
    case 'stale':
      return summary.staleCount;
    case 'cold':
      return summary.coldCount;
    case 'notSeen':
      return summary.notSeenCount;
  }
};

interface YardChecksSummaryCardProps {
  summary: YardChecksSummary;
}

const YardChecksSummaryCard = ({ summary }: YardChecksSummaryCardProps) => {
  const { totalYardSpots } = summary;
  const seenCount = totalYardSpots - summary.notSeenCount;
  const seenPct = totalYardSpots > 0 ? Math.round((seenCount / totalYardSpots) * 100) : 0;

  return (
    <Paper sx={{ p: 3 }}>
      <Typography
        variant="overline"
        sx={{ color: 'text.secondary', fontWeight: 500, letterSpacing: '0.08em' }}
      >
        Coverage
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mt: 1.5 }}>
        <Typography sx={{ fontSize: 48, fontWeight: 300, lineHeight: 1, fontFamily: 'monospace' }}>
          {seenCount}
        </Typography>
        <Typography sx={{ fontSize: 20, color: 'text.secondary' }}>/ {totalYardSpots}</Typography>
      </Box>

      <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1, maxWidth: '32ch' }}>
        yard spots with an RTLS read in the last 4 hours — {seenPct}% coverage.
      </Typography>

      <Box sx={{ display: 'flex', height: 8, mt: 2.5, borderRadius: 1, overflow: 'hidden', bgcolor: 'grey.200' }}>
        {BUCKETS.map(bucket => {
          const count = countFor(summary, bucket.key);
          return count > 0 ? (
            <Box key={bucket.key} sx={{ bgcolor: bucket.color, flexGrow: count, flexBasis: 0 }} />
          ) : null;
        })}
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', mt: 2.5 }}>
        {BUCKETS.map(bucket => (
          <Box
            key={bucket.key}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              py: 1.25,
              borderTop: 1,
              borderColor: 'divider',
            }}
          >
            <Box sx={{ width: 12, height: 12, flexShrink: 0, borderRadius: '50%', bgcolor: bucket.color }} />
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {bucket.label}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {bucket.range}
            </Typography>
            <Typography sx={{ ml: 'auto', fontFamily: 'monospace', fontSize: 16, fontWeight: 500 }}>
              {countFor(summary, bucket.key)}
            </Typography>
          </Box>
        ))}
      </Box>
    </Paper>
  );
};

export default YardChecksSummaryCard;
